/**
 * Productions for the views (PLAN §5.1, D22): the list, one production with
 * its config snapshot, create, end, the playlists a production can run, and
 * the as-run log in pages. The data-hook pattern (`rundowns.ts`).
 *
 * A create carries a client-minted `productionId`, so a retry (the failover
 * client resends it, `connectivity/failover.ts`) returns the production it
 * already made instead of making a second.
 */
import { allPages, type ApiClient } from '@acp/api-client/client.js';
import type { AsRunEvent } from '@acp/api-contract/playout.js';
import type { Production, ProductionSummary } from '@acp/api-contract/productions.js';
import type { SwitchoverResult } from '@acp/api-contract/redundancy.js';
import type { Row } from '@acp/domain/row.js';
import { infiniteQueryOptions, type QueryClient, queryOptions } from '@tanstack/react-query';

export const productionKeys = {
  all: ['productions'] as const,
  list: () => [...productionKeys.all, 'list'] as const,
  one: (productionId: string) => [...productionKeys.all, 'one', productionId] as const,
  asRun: (productionId: string) => [...productionKeys.all, 'asrun', productionId] as const,
};

/** Every production, newest first (all pages). */
export function productionsQuery(api: ApiClient) {
  return queryOptions({
    queryKey: productionKeys.list(),
    queryFn: async (): Promise<ProductionSummary[]> =>
      allPages(async (pageToken) => {
        const page = await api.call('listProductions', { params: null, query: pageToken === undefined ? {} : { pageToken }, body: null });
        return { items: page.productions, nextPageToken: page.nextPageToken };
      }),
  });
}

export function productionQuery(api: ApiClient, productionId: string) {
  return queryOptions({
    queryKey: productionKeys.one(productionId),
    queryFn: async (): Promise<Production> => (await api.call('getProduction', { params: { productionId }, query: null, body: null })).production,
  });
}

/** What a playlist picker shows. */
export type PlaylistSummary = { readonly playlistId: string; readonly name: string };

function nameOf(row: Row): string {
  const name = row.f['name'];
  return typeof name === 'string' && name !== '' ? name : '(unnamed)';
}

export function playlistsQuery(api: ApiClient) {
  return queryOptions({
    queryKey: ['playlists', 'list'] as const,
    queryFn: async (): Promise<PlaylistSummary[]> => {
      const rows = await allPages(async (pageToken) => {
        const page = await api.call('listPlaylists', { params: null, query: pageToken === undefined ? {} : { pageToken }, body: null });
        return { items: page.playlists, nextPageToken: page.nextPageToken };
      });
      return rows.map((r) => ({ playlistId: r.rowId, name: nameOf(r) })).sort((a, b) => a.name.localeCompare(b.name));
    },
  });
}

export type NewProduction = {
  readonly studioId: string;
  /** The show style it runs (D32); null: the studio's default. */
  readonly showStyleId: string | null;
  readonly source: { readonly kind: 'rundown'; readonly rundownId: string } | { readonly kind: 'playlist'; readonly playlistId: string };
  readonly rehearsal: boolean;
};

/** `newId`: a ULID factory (the production's id is the client's, for an idempotent retry). */
export function createProductionMutation(api: ApiClient, queryClient: QueryClient, newId: () => string) {
  return {
    mutationFn: async (input: NewProduction): Promise<Production> => {
      const body = {
        productionId: newId(),
        studioId: input.studioId,
        ...(input.showStyleId === null ? {} : { showStyleId: input.showStyleId }),
        rundownId: input.source.kind === 'rundown' ? input.source.rundownId : null,
        playlistId: input.source.kind === 'playlist' ? input.source.playlistId : null,
        rehearsal: input.rehearsal,
      };
      return (await api.call('createProduction', { params: null, query: null, body })).production;
    },
    onSuccess: async (production: Production): Promise<void> => {
      queryClient.setQueryData(productionKeys.one(production.productionId), production);
      await queryClient.invalidateQueries({ queryKey: productionKeys.list() });
    },
  };
}

export function endProductionMutation(api: ApiClient, queryClient: QueryClient) {
  return {
    mutationFn: async (input: { readonly productionId: string; readonly reason: string | null }): Promise<Production> =>
      (await api.call('endProduction', { params: { productionId: input.productionId }, query: null, body: { reason: input.reason } })).production,
    onSuccess: async (production: Production): Promise<void> => {
      queryClient.setQueryData(productionKeys.one(production.productionId), production);
      await queryClient.invalidateQueries({ queryKey: productionKeys.list() });
    },
  };
}

/**
 * A manual switchover (PLAN §8.4) — the node's route: either node of the pair answers. The playout state that
 * follows says who is active now (its `redundancy`), so nothing is cached here.
 */
export function switchoverMutation(api: ApiClient) {
  return {
    mutationFn: async (input: { readonly productionId: string }): Promise<SwitchoverResult> =>
      api.call('switchoverProduction', { params: { productionId: input.productionId }, query: null, body: {} }),
  };
}

/** One page of the as-run log. */
export type AsRunPage = { readonly events: readonly AsRunEvent[]; readonly cursor: string | null; readonly more: boolean };

/** Events per as-run page. */
export const AS_RUN_PAGE = 100;

/** The first page's cursor: none (from the start). Typed wide so later pages' cursors fit. */
const FROM_START: string | null = null;

/**
 * The as-run log, oldest first, a page at a time: each page continues from
 * the previous one's cursor. The last page is re-read to see new events
 * (`refetch`), since the log only grows at its end.
 */
export function asRunQuery(api: ApiClient, productionId: string) {
  return infiniteQueryOptions({
    queryKey: productionKeys.asRun(productionId),
    initialPageParam: FROM_START,
    queryFn: async ({ pageParam }): Promise<AsRunPage> => {
      const query = pageParam === null ? { limit: AS_RUN_PAGE } : { cursor: pageParam, limit: AS_RUN_PAGE };
      return api.call('listAsRun', { params: { productionId }, query, body: null });
    },
    getNextPageParam: (last: AsRunPage): string | null => (last.more ? last.cursor : null),
  });
}
